import { useCallback, useEffect, useState } from "react";

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

const STATUSES = ["proposed", "active", "completed", "declined"];

function Pool({ title, people, empty }) {
  return (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div className="l" style={{ color: "var(--muted)", fontSize: 13, marginBottom: 8 }}>{title}</div>
      {people.length ? (
        people.map((p) => (
          <div className="card" key={p.member_id} style={{ padding: 12, marginBottom: 8 }}>
            <strong>{p.name}</strong>
            {p.focus_areas && <div className="muted" style={{ fontSize: 13 }}>{p.focus_areas}</div>}
            {p.experience && <div className="muted" style={{ fontSize: 12 }}>{p.experience}</div>}
            {p.availability && <div className="muted" style={{ fontSize: 12 }}>Available: {p.availability}</div>}
          </div>
        ))
      ) : (
        <p className="empty">{empty}</p>
      )}
    </div>
  );
}

export function Mentorship({ chapterId, canManage }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api(`/api/chapters/${chapterId}/mentorship`)
      .then((res) => (res.ok ? res.json() : null))
      .then(setData);
  }, [chapterId]);
  useEffect(load, [load]);

  async function pair(mentor_member_id, mentee_member_id) {
    setErr("");
    const res = await api(`/api/chapters/${chapterId}/mentorships`, {
      method: "POST",
      body: JSON.stringify({ mentor_member_id, mentee_member_id }),
    });
    if (res.ok) load();
    else setErr((await res.json().catch(() => ({}))).error || "Could not create pairing.");
  }
  async function setStatus(id, status) {
    await api(`/api/mentorships/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    load();
  }
  async function remove(id) {
    if (!window.confirm("Delete this pairing?")) return;
    await api(`/api/mentorships/${id}`, { method: "DELETE" });
    load();
  }

  if (!canManage) return <p className="muted">Mentorship coordination is available to the committee.</p>;
  if (data === null) return <p className="muted">Loading mentorship…</p>;

  return (
    <div className="section">
      <div className="section-head"><h3>Mentor & mentee pool</h3></div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Members opt in from their account. Suggested matches share at least one focus area.
      </p>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Pool title={`Offering to mentor · ${data.mentors.length}`} people={data.mentors} empty="No mentors yet." />
        <Pool title={`Seeking a mentor · ${data.mentees.length}`} people={data.mentees} empty="No mentees yet." />
      </div>

      <div className="section-head" style={{ marginTop: 20 }}><h3>Suggested matches</h3></div>
      {err && <p className="err">{err}</p>}
      {data.suggestions.length ? (
        <table className="tbl">
          <thead><tr><th>Mentee</th><th>Mentor</th><th>Shared focus</th><th /></tr></thead>
          <tbody>
            {data.suggestions.map((s, i) => (
              <tr key={i}>
                <td>{s.mentee_name}</td>
                <td>{s.mentor_name}</td>
                <td>{s.overlap.map((o) => <span className="pill" key={o} style={{ marginRight: 4 }}>{o}</span>)}</td>
                <td className="row-actions">
                  <button className="btn-primary" style={{ width: "auto" }} type="button" onClick={() => pair(s.mentor_member_id, s.mentee_member_id)}>Pair</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">No suggested matches — need overlapping focus areas between an active mentor and mentee.</p>
      )}

      <div className="section-head" style={{ marginTop: 20 }}><h3>Pairings · {data.pairings.length}</h3></div>
      {data.pairings.length ? (
        <table className="tbl">
          <thead><tr><th>Mentor</th><th>Mentee</th><th>Status</th><th>Started</th><th /></tr></thead>
          <tbody>
            {data.pairings.map((p) => (
              <tr key={p.id}>
                <td>{p.mentor_name}</td>
                <td>{p.mentee_name}</td>
                <td>
                  <select value={p.status} onChange={(e) => setStatus(p.id, e.target.value)} style={{ width: "auto" }}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td>{p.started_on || "—"}</td>
                <td className="row-actions">
                  <button className="btn-danger" type="button" onClick={() => remove(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">No pairings yet.</p>
      )}
    </div>
  );
}
